// Import statements
import { categories } from './categories';
import { categoryIllustrations } from './illustrations';
import { sentencesToConstruct } from './sentences';

import type { LanguageData, LanguageCode, WordData } from '../../../types/types';

// Configuration spécifique à Kama
export const kamaConfig = {
  code: 'ka' as LanguageCode,
  name: 'Kama',
  nativeName: '𓆎𓅓𓏏𓊖',
  audioBasePath: '/audio/kama/',
  illustrationBasePath: '/illustrations/kama/',
  defaultCategories: [
    'Nombres',
    'Verbes',
    'Animaux',
    'Famille',
    'Corps',
    'Couleurs',
    'Nourriture',
    'Temps',
    'Objets sacrés et du quotidien',
    'Divinités',
    'Villes',
    'Pharaons',
    'Plantes',
  ],
  revisionSettings: {
    initialInterval: 24 * 60 * 60 * 1000,
    easeFactor: 2.5,
    minimumInterval: 24 * 60 * 60 * 1000,
    maximumInterval: 365 * 24 * 60 * 60 * 1000,
  },
};

// Données principales de la langue Kama
export const kama: LanguageData = {
  code: kamaConfig.code,
  name: kamaConfig.name,
  nativeName: kamaConfig.nativeName,
  categories,
  categoryIllustrations,
  sentencesToConstruct,
  // Non consommé par l'app à l'exécution (getLanguageData lit l'objet
  // équivalent dans data/languages/index.ts) — gardé aligné pour éviter
  // toute confusion future entre les deux définitions.
  sentenceCategories: {
    'Salutations': {
      icon: '𓋴',
      description: 'Premières phrases hiéroglyphiques, à partir des règles apprises dans Phrase',
    },
    'Présentation': {
      icon: '𓇋',
      description: 'Se présenter avec ink, « moi »',
    },
    'Famille et relations': {
      icon: '𓅭',
      description: 'Père, mère, fils et fille, épouse du roi',
    },
    'Vie quotidienne': {
      icon: '𓏏',
      description: 'La maison, les animaux et le corps',
    },
    'Au marché': {
      icon: '𓇋',
      description: 'Pain, bière, vin et autres denrées',
    },
    'Voyages': {
      icon: '𓇳',
      description: 'Le Nil, les villes, les jours et les nuits',
    },
    'Panthéon et royaume': {
      icon: '𓊹',
      description: 'Épithètes des dieux et titres royaux',
    },
  },
};

// Fonctions utilitaires spécifiques à Kama
export const getKamaAudioPath = (filename: string): string =>
  `${kamaConfig.audioBasePath}${filename}`;

export const getKamaIllustrationPath = (filename: string): string =>
  `${kamaConfig.illustrationBasePath}${filename}`;

export const isValidKamaCategory = (category: string): boolean =>
  Object.keys(categories).includes(category);

export const getKamaCategoryWords = (category: string): Record<string, WordData> | null => {
  if (!isValidKamaCategory(category)) {
    throw new Error(`Category ${category} does not exist in Kama`);
  }

  const categoryData = categories[category];

  const wordData = Object.entries(categoryData).reduce((acc, [key, value]) => {
    if ('translation' in value) {
      acc[key] = value as WordData;
    }
    return acc;
  }, {} as Record<string, WordData>);

  return wordData;
};

// Export des catégories et illustrations pour un accès direct
export { categories, categoryIllustrations, sentencesToConstruct };

export type KamaCategories = keyof typeof categories;

export default kama;

export type KamaLanguageData = typeof kama;
